import { useMemo, useState } from "react";
import { Box, Button } from "@mui/material";
import { useQuery, useQueryClient } from "react-query";
import PageShell from "../components/new/PageShell";
import { useSettingSearch } from "../hooks/useSettingSearch";
import { useSettingGroups } from "../hooks/useSettings";
import { GROUP_MAP, SYSTEM_GROUP } from "../settings/groups";
import SettingSearchBar from "../components/setting/SettingSearchBar";
import { SettingsGate } from "../components/SettingGate";
import SettingsNav from "../components/setting/SettingsNav";
import SettingsPanel from "../components/setting/SettingsPanel";
import SettingsSaveBar from "../components/setting/SettingsSaveBar";
import SWStatusSection from "../components/setting/SWStatusSection";
import DevModeSection from "../components/config/DevModeSection";
import WebConfigModal from "../components/setting/WebConfigModal";
import { getWebConfigAll, patchWebConfig } from "../services/webConfig";

export default function UserSettingPage() {
  const { query, results, search, navigateTo, highlightedId } = useSettingSearch();
  const [configOpen, setConfigOpen] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const { data: webConfig } = useQuery("config-all", getWebConfigAll);
  const queryClient = useQueryClient();

  const groups = useSettingGroups();
  const navItems = useMemo(
    () => [...groups.map(({ group }) => GROUP_MAP[group] ?? { id: group, title: group, icon: null }), SYSTEM_GROUP],
    [groups],
  );
  const active = selected && navItems.some((i) => i.id === selected) ? selected : navItems[0]?.id;

  return (
    <PageShell title="설정" subtitle="화면 표시 방식과 시스템 상태를 관리합니다.">
      <Box sx={{ maxWidth: 520, mb: 3 }}>
        <SettingSearchBar
          query={query}
          results={results}
          onSearch={search}
          onNavigate={(item) => {
            setSelected(item.sectionId);
            navigateTo(item);
          }}
        />
      </Box>

      <SettingsGate>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "200px minmax(0, 1fr)" },
            gap: 3,
            alignItems: "start",
          }}
        >
          <SettingsNav items={navItems} active={active} onSelect={setSelected} />

          <Box sx={{ maxWidth: 720 }}>
            {active === SYSTEM_GROUP.id ? (
              <>
                <SWStatusSection />
                {/* TODO: 관리자 권한 체크 */}
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                  <Button size="small" variant="outlined" disabled={!webConfig} onClick={() => setConfigOpen(true)}>
                    전체 옵션 보기
                  </Button>
                </Box>
                <DevModeSection />
              </>
            ) : (
              active && <SettingsPanel group={active} highlightedRowId={highlightedId} />
            )}
          </Box>
        </Box>
      </SettingsGate>

      <SettingsSaveBar />

      <WebConfigModal
        open={configOpen}
        config={webConfig ?? {}}
        onClose={() => setConfigOpen(false)}
        onSave={async (patch) => {
          const saved = await patchWebConfig(patch);
          queryClient.setQueryData("config-all", saved);
          setConfigOpen(false);
          window.location.reload();
        }}
      />
    </PageShell>
  );
}
